/**
 * @author pjh
 * @date 2025.07.31
 * @description 복비 주문서 상세 모달
 */
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
    getBokbiPurchaseOrder,
    IBokbiPurchaseOrder,
    agreePurchaseOrder,
} from '../../../../service/bokbiAPI';
import { toast } from 'react-toastify';
import './BokbiPurchaseOrderModal.css';
import { useLoginStore } from '../../../../store/useLogin';
import Input from "../../../form/Input";
import {isAxiosError} from "axios";
import {ResponseError} from "../../../../type/type";

interface IBokbiPurchaseOrderModal {
    isOpen: boolean;
    onClose: () => void;
    purchaseOrderId: string;
    onSuccess?: () => void;
}
const BokbiPurchaseOrderModal: React.FC<IBokbiPurchaseOrderModal> = ({ isOpen, onClose, purchaseOrderId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState<IBokbiPurchaseOrder | null>(null);
    const [memo, setMemo] = useState<string>('');
    const { role } = useLoginStore();
    useEffect(() => {
        if (isOpen && purchaseOrderId) {
            fetchPurchaseOrder();
        }
    }, [isOpen, purchaseOrderId]);

    const fetchPurchaseOrder = async () => {
        setLoading(true);
        try {
            const data = await getBokbiPurchaseOrder(purchaseOrderId);
            setOrderData(data);
        } catch (error) {
            console.error('주문서 조회 실패:', error);
            toast.error('주문서 정보를 불러오는데 실패했습니다.');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatAmount = (amount: number) => {
        return amount.toLocaleString('ko-KR') + '원';
    };

    const handleClose = () => {
        setOrderData(null);
        setMemo('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="saju_modal_overlay" onClick={handleClose}>
            <div className="saju_purchase_order_modal_content" onClick={(e) => e.stopPropagation()}>
                <div className="saju_modal_header">
                    <h2>복비 충전 주문서</h2>
                    <button className="saju_modal_close" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="saju_modal_body">
                    {loading ? (
                        <div className="saju_loading_container">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                            <p>주문서 정보를 불러오는 중...</p>
                        </div>
                    ) : orderData ? (
                        <div className="saju_purchase_order_info">
                            <div className="saju_order_item">
                                <label>입금자명</label>
                                <span>{orderData.depositName || '-'}</span>
                            </div>
                            <div className="saju_order_item">
                                <label>입금 은행</label>
                                <span>{orderData.depositBank || '-'}</span>
                            </div>
                            <div className="saju_order_item">
                                <label>입금 금액</label>
                                <span className="saju_order_amount">{formatAmount(orderData.depositAmount)}</span>
                            </div>
                            <div className="saju_order_item">
                                <label>메모</label>
                                <span>{orderData.description || '-'}</span>
                            </div>
                            <div className="saju_order_item">
                                <label>승인 신청일</label>
                                <span>{formatDate(orderData.createdAt)}</span>
                            </div>
                            
                            <div className="saju_tax_invoice_section">
                                <h3>세금계산서 발행 정보</h3>
                                <div className="saju_tax_invoice_info">
                                    <div className="saju_order_item">
                                        <label>사업자명</label>
                                        <span>{orderData.companyName || '-'}</span>
                                    </div>
                                    <div className="saju_order_item">
                                        <label>사업자번호</label>
                                        <span>{orderData.companyNumber || '-'}</span>
                                    </div>
                                    <div className="saju_order_item">
                                        <label>대표자명</label>
                                        <span>{orderData.ceo || '-'}</span>
                                    </div>
                                    <div className="saju_order_item">
                                        <label>사업장주소</label>
                                        <span>{orderData.address || '-'}</span>
                                    </div>
                                    <div className="saju_order_item">
                                        <label>이메일</label>
                                        <span>{orderData.email || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="saju_no_data">
                            <p>주문서 정보를 불러올 수 없습니다.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BokbiPurchaseOrderModal;